import {AxiosError} from 'axios';

/**
 * Flattened, analytics-friendly representation of a checkout error.
 *
 * AxiosError instances don't serialize cleanly through Segment/Amplitude —
 * non-enumerable properties get dropped, and the response body is nested.
 * This helper pulls the useful fields (especially the Stripe decline info
 * the Hypnos endpoints forward in their 400 response bodies) onto a flat
 * object so they show up as first-class properties in Amplitude.
 */
export interface CheckoutErrorFields {
  errorMessage: string;
  backendError?: string;
  detailedMessage?: string;
  stripeCode?: string;
  stripeType?: string;
  stripeDeclineCode?: string;
  stripeAdviceCode?: string;
  httpStatus?: number;
  axiosCode?: string;
  responseBody?: string;
}

type StripeCheckoutErrorBody = {
  error?: string;
  detailed_message?: string;
  code?: string;
  type?: string;
  decline_code?: string;
  advice_code?: string;
};

export const extractCheckoutError = (error: unknown): CheckoutErrorFields => {
  const fields: CheckoutErrorFields = {errorMessage: 'Unknown error'};

  if (!error || typeof error !== 'object') {
    return fields;
  }

  const axiosError = error as AxiosError<StripeCheckoutErrorBody | string>;

  if (axiosError.response) {
    fields.httpStatus = axiosError.response.status;
  }
  if (axiosError.code) {
    fields.axiosCode = axiosError.code;
  }
  if (axiosError.message) {
    fields.errorMessage = axiosError.message;
  }

  const data = axiosError.response?.data;

  // Hypnos returns three shapes from the checkout endpoints:
  //  1) Plain string (e.g. "User already subscribed")
  //  2) { error: "Could not register user" } for validation failures
  //  3) { error, detailed_message, code, type, decline_code, advice_code } for Stripe errors
  if (typeof data === 'string') {
    fields.backendError = data;
    fields.responseBody = data;
    fields.errorMessage = data;
  } else if (data && typeof data === 'object') {
    fields.backendError = data.error;
    fields.detailedMessage = data.detailed_message;
    fields.stripeCode = data.code;
    fields.stripeType = data.type;
    fields.stripeDeclineCode = data.decline_code;
    fields.stripeAdviceCode = data.advice_code;
    fields.errorMessage = data.detailed_message || data.error || fields.errorMessage;
    try {
      fields.responseBody = JSON.stringify(data);
    } catch {
      // Ignore serialization failures — responseBody is optional.
    }
  }

  return fields;
};
